import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';

import { Modal } from './modal';
import { ConfirmOptions } from './models/modal.model';

/**
 * Internal host for `ModalService.confirm(...)`. Composes the modal shell with a
 * message paragraph and an optional type-to-confirm input. Not exported for
 * direct template use — always reach it through the service.
 */
@Component({
  selector: 'app-confirm-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Modal, TranslocoDirective],
  template: `
    <ng-container *transloco="let t">
      <app-modal
        [(open)]="open"
        [title]="options().title"
        [kind]="options().kind ?? 'warning'"
        [size]="options().size ?? 'small'"
        [primaryLabel]="options().confirmLabel ?? t('common.confirm')"
        [cancelLabel]="options().cancelLabel ?? null"
        [primaryDestructive]="!!options().destructive"
        [primaryDisabled]="!canConfirm()"
        (primaryAction)="confirm()"
        (closed)="cancel()"
      >
        <div class="flex flex-col gap-4">
          <p class="text-[15px] leading-relaxed text-ink-2">{{ options().message }}</p>

          @if (options().confirmWord; as word) {
            <div
              class="flex flex-col gap-2 rounded-card bg-danger-surface p-3.5 shadow-hairline"
            >
              <span class="text-[13px] text-ink-2">
                {{ t('common.typeToConfirm', { word }) }}
              </span>
              <input
                type="text"
                [value]="typed()"
                (input)="typed.set($any($event.target).value)"
                [placeholder]="word"
                class="w-full rounded-control border border-hairline bg-card px-3 py-2 text-sm text-ink-1 outline-none focus:ring-2 focus:ring-focus"
              />
            </div>
          }
        </div>
      </app-modal>
    </ng-container>
  `,
})
export class ConfirmModal {
  readonly options = input.required<ConfirmOptions>();
  /** Emits true if confirmed, false if dismissed. The service tears down on this. */
  readonly result = output<boolean>();

  protected readonly open = signal(true);
  protected readonly typed = signal('');

  /** Primary enabled unless a confirmWord is set and not yet matched. */
  protected readonly canConfirm = computed(() => {
    const word = this.options().confirmWord;
    return !word || this.typed().trim() === word;
  });

  protected confirm(): void {
    if (!this.canConfirm()) return;
    this.open.set(false);
    this.result.emit(true);
  }

  protected cancel(): void {
    this.open.set(false);
    this.result.emit(false);
  }
}
